import { ListStarIcon, ThumbsDownIcon, ThumbsUpIcon, XIcon } from '@phosphor-icons/react'
import * as Dialog from '@radix-ui/react-dialog'
import * as Popover from '@radix-ui/react-popover'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { match, P } from 'ts-pattern'
import z from 'zod'
import { useShowMutationError } from '../../hooks/use-show-mutation-error.tsx'
import { useToastService } from '../../services/toast-service.tsx'
import { ApiError } from '../../utils/api-client/api-error.ts'
import {
  invalidateGetFixFeedbackSubmissions,
  invalidateGetScaFeedbackSubmissions,
  invalidateGetTriageFeedbackSubmissions,
  useGetFixFeedbackSubmissions,
  useGetProjectFromHalLink,
  useGetScaFeedbackSubmissions,
  useGetScan,
  useGetTriageFeedbackSubmissions,
  usePostFixFeedbackSubmission,
  usePostScaFeedbackSubmission,
  usePostTriageFeedbackSubmission,
} from '../../utils/api-client/api-hooks.ts'
import { type FeedbackSubmissionResponse, getRepositoryProject } from '../../utils/api-client/api-schemas.ts'
import { formatDate } from '../../utils/date-utils.ts'
import { pluralize } from '../../utils/text-utils.ts'
import * as styles from './feedback-bar.css.ts'
import { FeedbackForm, type FeedbackScopeContext, getSubmissionScope, useFeedbackForm } from './feedback-form.tsx'
import { FloatingFooter } from './floating-footer.tsx'

// #region Types

export const FeedbackResultTypeSchema = z.enum(['sca', 'triage', 'fix'])
export type FeedbackResultType = z.infer<typeof FeedbackResultTypeSchema>

/**
 * The problem `POST .../feedback-submissions` raises when the repository turned out to be in no
 * project. Keyed on by type rather than on a bare 422, which any other validation failure also uses.
 */
export const REPOSITORY_UNGROUPED_PROBLEM_TYPE = 'pixee:problems/api/feedback-submissions/repository-ungrouped'
export const PROJECT_CHANGED_PROBLEM_TYPE = 'pixee:problems/api/feedback-submissions/project-changed'

export type FeedbackBarProps = {
  resultId: string
  resultType: FeedbackResultType
  /** The scan the result belongs to; it leads to the repository, whose project bounds the feedback's scope. */
  scanId?: string
  drawerContentRef: React.RefObject<HTMLDivElement | null>
}

// #endregion

// #region Hooks

const useFeedback = ({ resultId, resultType }: { resultId: string; resultType: FeedbackResultType }) => {
  const triageQuery = useGetTriageFeedbackSubmissions({ resultId, enabled: resultType === 'triage' })
  const fixQuery = useGetFixFeedbackSubmissions({ resultId, enabled: resultType === 'fix' })
  const scaQuery = useGetScaFeedbackSubmissions({ resultId, enabled: resultType === 'sca' })
  const triageMutation = usePostTriageFeedbackSubmission()
  const fixMutation = usePostFixFeedbackSubmission()
  const scaMutation = usePostScaFeedbackSubmission()
  const queryClient = useQueryClient()

  return match(resultType)
    .with('triage', () => ({
      query: triageQuery,
      mutation: triageMutation,
      invalidate: () => invalidateGetTriageFeedbackSubmissions(queryClient, { resultId }),
    }))
    .with('fix', () => ({
      query: fixQuery,
      mutation: fixMutation,
      invalidate: () => invalidateGetFixFeedbackSubmissions(queryClient, { resultId }),
    }))
    .with('sca', () => ({
      query: scaQuery,
      mutation: scaMutation,
      invalidate: () => invalidateGetScaFeedbackSubmissions(queryClient, { resultId }),
    }))
    .exhaustive()
}

// #endregion

// #region FeedbackBar

/**
 * What the scope selector describes: the repository and its project (with member count). Resolved
 * from the scan lazily; until the repository arrives — or if it cannot be read — there is no context,
 * so the selector shows Project disabled and the submission carries no scope.
 */
const useFeedbackScopeContext = ({
  scanId,
  enabled,
  projectRefusedAt,
}: {
  scanId?: string
  /** The modal being open; nothing is fetched for a bar that is never rated. */
  enabled: boolean
  /** When a submission was last refused because the repository is in no project. */
  projectRefusedAt: number | undefined
}): { scopeContext: FeedbackScopeContext | undefined; refresh: () => unknown } => {
  const {
    data: scan,
    dataUpdatedAt,
    refetch,
  } = useGetScan({
    id: scanId,
    embed: ['repository.project-membership.project'],
    enabled,
  })
  const repository = scan?._embedded?.repository
  // The embedded project carries name and member count together, so the scope selector needs no
  // second fetch to describe the project the repository belongs to.
  const project = repository && getRepositoryProject(repository)
  // A refusal answers the membership read before it. `dataUpdatedAt` moves only on a successful
  // read — a failed refetch keeps the old data and its old time — so any later read that succeeds
  // supersedes the refusal, whether it shows a project (Project returns) or not (ungrouped says why).
  const isProjectRefused = projectRefusedAt !== undefined && dataUpdatedAt <= projectRefusedAt
  // Refetches only this query: invalidating the scan's key from outside would also refetch the scan
  // details page's copy, which does not embed the repository and says nothing about its project.
  const refresh = () => void refetch()

  if (!enabled || !scanId || !repository) {
    return { scopeContext: undefined, refresh }
  }
  return {
    scopeContext: {
      repositoryName: repository.full_name,
      project: project ? { id: project.id, name: project.name, repositoryCount: project.repository_count } : undefined,
      isProjectRefused,
    },
    refresh,
  }
}

export const FeedbackBar: React.FC<FeedbackBarProps> = ({ resultId, resultType, scanId, drawerContentRef }) => {
  const form = useFeedbackForm()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [projectRefusedAt, setProjectRefusedAt] = useState<number>()
  const { scopeContext, refresh: refreshScopeContext } = useFeedbackScopeContext({
    scanId,
    enabled: isModalOpen,
    projectRefusedAt,
  })

  const { addToast } = useToastService()
  const showMutationError = useShowMutationError({ prefix: 'Memory could not be updated' })

  const {
    query: { data: existingFeedback },
    mutation: { mutate: postSubmission, isPending: isSubmitting },
    invalidate,
  } = useFeedback({ resultId, resultType })
  // Backend enforces at most one submission per result (POST returns 409 on
  // duplicate), so `items` is always 0 or 1 in length.
  const submission = existingFeedback?._embedded?.items?.[0]
  const isSubmitted = !!submission

  const handleBarIconClick = () => {
    if (isSubmitted) {
      setIsPopoverOpen(prev => !prev)
    } else {
      setIsModalOpen(true)
    }
  }

  const handleSubmit = () => {
    const { rating, text, scope } = form.store.state.values
    if (!rating) {
      return
    }
    postSubmission(
      { resultId, rating, text, ...getSubmissionScope({ scope, scopeContext }) },
      {
        onSuccess: () => {
          setIsModalOpen(false)
          form.reset()
          addToast({ message: <>Memory successfully updated</>, variant: 'success' })
        },
        onError: (error, ...args) => {
          // The status alone does not say which 409 this is: only an untyped one is
          // first-submission-wins, so the problem type is what each branch keys on.
          const problem = error instanceof ApiError ? { status: error.status, type: error.problemType } : undefined
          match(problem)
            .with({ status: 409, type: PROJECT_CHANGED_PROBLEM_TYPE }, () => {
              // The repository moved under the open form. Keep everything the author typed, refresh
              // what the selector describes so it names the project the repository is in now, and
              // ask them to confirm that reach.
              refreshScopeContext()
              addToast({
                message: (
                  <>This repository moved to another project. Check the new project&apos;s reach, then submit again.</>
                ),
                variant: 'error',
              })
            })
            .with({ status: 409, type: P.union(undefined, 'about:blank') }, () => {
              invalidate()
              setIsModalOpen(false)
              form.reset()
              addToast({ message: <>Feedback was already submitted for this result</>, variant: 'info' })
            })
            .with({ status: 422, type: REPOSITORY_UNGROUPED_PROBLEM_TYPE }, () => {
              // Membership changed under the open modal. The form is kept, Project goes dead until the
              // next successful read, and the scope falls back to the repository so nothing is
              // submitted at a scope that no longer exists.
              setProjectRefusedAt(Date.now())
              form.setFieldValue('scope', 'repository')
              refreshScopeContext()
              addToast({
                message: (
                  <>
                    This repository isn&apos;t in a project, so the feedback cannot be scoped to one. Choose another
                    scope.
                  </>
                ),
                variant: 'error',
              })
            })
            .otherwise(() => showMutationError(error, ...args))
        },
      }
    )
  }

  const handleModalClose = () => {
    setIsModalOpen(false)
    form.reset()
    setProjectRefusedAt(undefined)
  }

  return (
    <form.AppForm>
      <FloatingFooter drawerContentRef={drawerContentRef} className={styles.feedbackFooter}>
        <div className={styles.barGroup}>
          <span
            className={isSubmitted ? styles.barLabelRated : styles.barLabel}
            role="button"
            tabIndex={0}
            onClick={handleBarIconClick}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                handleBarIconClick()
              }
            }}
          >
            Was this helpful?
          </span>

          {isSubmitted ? (
            <SubmittedBarIcon
              submission={submission}
              isPopoverOpen={isPopoverOpen}
              onIconClick={handleBarIconClick}
              onPopoverClose={() => setIsPopoverOpen(false)}
            />
          ) : (
            <button className={styles.barIconButton} onClick={handleBarIconClick} aria-label="Rate this result">
              <ListStarIcon size={16} />
            </button>
          )}
        </div>
      </FloatingFooter>

      <FeedbackModal
        isOpen={isModalOpen}
        isSubmitting={isSubmitting}
        onSubmit={handleSubmit}
        onClose={handleModalClose}
        scopeContext={scopeContext}
      />
    </form.AppForm>
  )
}

// #endregion

// #region SubmittedBarIcon

type SubmittedBarIconProps = {
  submission: FeedbackSubmissionResponse | undefined
  isPopoverOpen: boolean
  onIconClick: () => unknown
  onPopoverClose: () => unknown
}

const SubmittedBarIcon: React.FC<SubmittedBarIconProps> = ({
  submission,
  isPopoverOpen,
  onIconClick,
  onPopoverClose,
}) => (
  <Popover.Root
    open={isPopoverOpen}
    onOpenChange={open => {
      if (!open) onPopoverClose()
    }}
  >
    <Popover.Anchor asChild>
      <button
        className={styles.barIconButtonRated}
        onClick={onIconClick}
        aria-label={
          submission?.rating === 'positive' ? 'View submitted positive feedback' : 'View submitted negative feedback'
        }
      >
        {submission?.rating === 'positive' ? (
          <ThumbsUpIcon size={16} weight="fill" />
        ) : (
          <ThumbsDownIcon size={16} weight="fill" />
        )}
      </button>
    </Popover.Anchor>
    <Popover.Portal>
      <Popover.Content className={styles.popoverContent} side="top" align="end" sideOffset={8}>
        <div className={styles.popoverHeader}>
          <p className={styles.popoverMeta}>
            {submission?.createdAt && formatDate({ date: submission.createdAt, type: 'mm/dd/yyyy-hh:mm-ss' })}
            {submission?.username && <span> by @{submission.username}</span>}
          </p>
          <button className={styles.popoverCloseButton} onClick={onPopoverClose} aria-label="Close">
            <XIcon size={16} />
          </button>
        </div>
        {submission && <SubmittedScope submission={submission} />}
        {submission?.text && <p className={styles.popoverText}>{submission.text}</p>}
      </Popover.Content>
    </Popover.Portal>
  </Popover.Root>
)

/**
 * Where the submitted feedback reaches. The scope comes back on the submission itself; a project
 * scope also carries `_links.project`, which is followed as given so the line can say how far "this
 * project" actually goes.
 */
const SubmittedScope: React.FC<{ submission: FeedbackSubmissionResponse }> = ({ submission }) => {
  const projectHref = submission._links?.project?.href
  const { data: project } = useGetProjectFromHalLink({
    path: projectHref,
    enabled: submission.scope === 'project',
  })
  const projectName = project?.name ?? submission._links?.project?.title
  const repositoryCount = project?.repository_count

  const line = match({ scope: submission.scope, projectName, repositoryCount })
    .with({ scope: 'project', projectName: undefined }, () => <>Applies to this project</>)
    .with({ scope: 'project', projectName: P.string, repositoryCount: undefined }, ({ projectName: name }) => (
      <>
        Applies to <b className={styles.popoverScopeSubject}>{name}</b>
      </>
    ))
    .with(
      { scope: 'project', projectName: P.string, repositoryCount: P.number },
      ({ projectName: name, repositoryCount: count }) => (
        <>
          Applies to <b className={styles.popoverScopeSubject}>{name}</b> ({count}{' '}
          {pluralize(count, 'repository', 'repositories')})
        </>
      )
    )
    .with({ scope: 'repository' }, () => <>Applies to this repository</>)
    .exhaustive()

  return <p className={styles.popoverScope}>{line}</p>
}

// #endregion

// #region FeedbackModal

export type FeedbackModalProps = {
  isOpen: boolean
  isSubmitting: boolean
  onSubmit: () => unknown
  onClose: () => unknown
  scopeContext?: FeedbackScopeContext
}

export const FeedbackModal: React.FC<FeedbackModalProps> = ({
  isOpen,
  isSubmitting,
  onSubmit,
  onClose,
  scopeContext,
}) => (
  <Dialog.Root
    open={isOpen}
    onOpenChange={open => {
      if (!open) onClose()
    }}
  >
    <Dialog.Portal>
      <Dialog.Overlay className={styles.modalOverlay} />
      <Dialog.Content className={styles.modalContent} onEscapeKeyDown={onClose} aria-describedby={undefined}>
        <Dialog.Title className={styles.modalTitle}>Was this helpful?</Dialog.Title>
        <p className={styles.modalSubtitle}>What is your overall opinion of this result?</p>
        <FeedbackForm isSubmitting={isSubmitting} onSubmit={onSubmit} onCancel={onClose} scopeContext={scopeContext} />
      </Dialog.Content>
    </Dialog.Portal>
  </Dialog.Root>
)

// #endregion
